import { useEffect } from 'react';
import { AppShell, Button } from '@dj/ui';
import { useClaim } from '../state/ClaimContext';

/**
 * Final step — success. The entitlement is already provisioned server-side
 * (claimEntitlement runs atomically — Stripe sub + Firestore writes
 * complete before this page renders). We just confirm + send the user to
 * the store with a reminder to use the same email at app sign-in.
 *
 * Clears ClaimContext 30 s after mount so a stale tab doesn't auto-restore
 * stale state into a fresh session.
 */
export function ConfirmationPage() {
  const { identity, result, reset } = useClaim();

  useEffect(() => {
    const t = window.setTimeout(() => { reset(); }, 30_000);
    return () => window.clearTimeout(t);
  }, [reset]);

  return (
    <AppShell title="You're all set">
      <div className="bg-white rounded-lg shadow p-6 sm:p-8 max-w-lg mx-auto text-center">
        <div className="text-6xl mb-3">✓</div>
        <h1 className="text-2xl font-semibold text-slate-900 mb-3">
          You're all set
        </h1>
        <p className="text-slate-700 mb-2">
          Your Doctor's Journal subscription is active
          {result?.providerName ? `, sponsored by ${result.providerName}` : ''}
          .
        </p>
        {identity?.email && (
          <p className="text-slate-700 mb-6">
            We sent a confirmation to <span className="font-semibold">{identity.email}</span>.
          </p>
        )}

        <div className="bg-teal-50 border border-teal-200 rounded p-4 text-left mb-6">
          <h2 className="font-semibold text-teal-900 mb-2">Next step — open the app</h2>
          <ol className="text-sm text-slate-700 space-y-1 list-decimal pl-4">
            <li>Install Doctor's Journal from the App Store or Google Play.</li>
            <li>
              Sign in with <span className="font-semibold">{identity?.email ?? 'this email'}</span> —
              your subscription links automatically.
            </li>
            <li>If you sign in with Apple, use the same email so your subscription comes with you.</li>
          </ol>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <a
            href="https://apps.apple.com/nz/app/doctors-journal/id6759405974"
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1"
          >
            <Button variant="primary" fullWidth>App Store</Button>
          </a>
          <a
            href="https://play.google.com/store/apps/details?id=nz.doctorsjournal.app"
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1"
          >
            <Button variant="secondary" fullWidth>Google Play</Button>
          </a>
        </div>
      </div>
    </AppShell>
  );
}
